/* eslint-disable react/no-array-index-key */
import { BitkitButton, BitkitDialog, BitkitList, BitkitNoteCard, createBitkitToast } from '@bitrise/bitkit-v2';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import useStartBuild from '@/hooks/useStartBuild';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';

import BuildConfiguration, { getFormDataToSend } from '../BuildConfiguration/BuildConfiguration';
import { StartBuildType } from './types';
import getBuildParams from './utils/getBuildParams';
import getSubmitButtonState from './utils/getSubmitButtonState';

interface StartBuildDialogProps {
  isOpen: boolean;
  onClose: () => void;
  app: AppDetailsPageProps['app'];
  buildTriggerToken: string;
  defaultBranch: string;
  pipelines?: string[];
  workflows?: string[];
  openScheduleBuildDialog: () => void;
}

const StartBuildDialog = (props: StartBuildDialogProps) => {
  const { app, buildTriggerToken, defaultBranch, isOpen, onClose, openScheduleBuildDialog } = props;

  const defaultValues: StartBuildType = useMemo(
    () => ({
      branch: defaultBranch || '',
      buildConfigType: 'basic',
      commitHash: '',
      commitMessage: '',
      environments: [],
      licensePoolId: '',
      machineTypeId: '',
      pipelineableId: '',
      secrets: [],
      tag: '',
      source: 'branch',
    }),
    [defaultBranch],
  );

  const { user } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const [errorMessage, setErrorMessage] = useState<ReactNode>('');

  const useFormMethods = useForm<StartBuildType>({
    defaultValues: {
      ...defaultValues,
      branch: defaultBranch,
    },
  });

  const { handleSubmit, reset, setValue, watch, resetField } = useFormMethods;

  const formData = watch();

  const { isPending: isStartLoading, mutate: startBuild } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken,
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
        if (data.results && data.results?.length > 1) {
          onClose();
        }
      },
      onError: (error) => {
        if (error.response?.data.errorMsg) {
          setErrorMessage(error.response?.data.errorMsg);
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          if (resultErrors.length === error.response?.data.results?.length) {
            setErrorMessage(
              <>
                <Text textStyle="body/lg/semibold">Failed to start builds:</Text>
                {resultErrors.map((err, index) => (
                  <BitkitList key={index}>
                    <BitkitList.Item>{err}</BitkitList.Item>
                  </BitkitList>
                ))}
              </>,
            );
          } else {
            onClose();
            createBitkitToast({
              titleText: 'Failed to start builds',
              messageText: (
                <>
                  {resultErrors.map((err) => (
                    <Text key={err} color="status/critical/text" textStyle="body/lg/regular" marginBlock="4">
                      {err}
                    </Text>
                  ))}
                </>
              ),
              variant: 'critical',
              dismissible: true,
            });
          }
        } else {
          setErrorMessage(error.response?.data.message || 'Unknown error');
        }
      },
    },
  );

  const onSubmit: SubmitHandler<StartBuildType> = (data) => {
    const dataToSend = getFormDataToSend(data);
    const buildParams = getBuildParams(dataToSend);

    startBuild({ buildParams, triggeredBy: `manual-${user.username}` });
  };

  useEffect(() => {
    if (!isOpen) {
      reset({
        ...defaultValues,
      });
      setErrorMessage('');
    }

    resetField('pipelineableId');
  }, [defaultValues, isOpen, reset, resetField]);

  useEffect(() => {
    setValue('machineTypeId', '');
    setValue('stack', '');
    setValue('pipelineableId', '');
  }, [setValue]);

  const { disabledText, isSubmitDisabled } = getSubmitButtonState(formData);

  const switchScheduleBuild = () => {
    onClose();
    openScheduleBuildDialog();
  };

  return (
    <BitkitDialog
      data-testid="start-build-dialog"
      footerButtons={
        <>
          <BitkitButton variant="tertiary" marginInlineEnd="auto" onClick={switchScheduleBuild}>
            Schedule builds
          </BitkitButton>
          <BitkitButton form="build_form" variant="secondary" onClick={onClose}>
            Close
          </BitkitButton>
          <BitkitButton
            disabledReason={disabledText}
            data-testid="start-build"
            form="build_form"
            state={isStartLoading ? 'loading' : isSubmitDisabled ? 'disabled' : undefined}
            type="submit"
          >
            Start build
          </BitkitButton>
        </>
      }
      footerContent={errorMessage && <BitkitNoteCard status="critical" message={errorMessage} />}
      open={isOpen}
      scrollBehavior="inside"
      title="Start build"
      onOpenChange={({ open }) => !open && onClose()}
    >
      <FormProvider {...useFormMethods}>
        <chakra.form id="build_form" onSubmit={handleSubmit(onSubmit)}>
          <BuildConfiguration app={app} buildTriggerToken={buildTriggerToken} showCommands />
        </chakra.form>
      </FormProvider>
    </BitkitDialog>
  );
};

export default StartBuildDialog;
